import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, errText } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", telefono: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const mismatch = f.confirm.length > 0 && f.password !== f.confirm;
  const [reqs, setReqs] = useState(null);
  useEffect(() => {
    api.get("/public/legal/registration-requirements").then((r) => setReqs(r.data)).catch(() => setReqs({ must_accept_terms: false, privacy: null, terms: null }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await register({
        first_name: f.first_name, last_name: f.last_name, email: f.email,
        telefono: f.telefono, password: f.password,
      });
      if (reqs?.must_accept_terms && reqs?.terms?.id) {
        try { await api.post("/legal/accept", { document_id: reqs.terms.id }); } catch { /* non-blocking */ }
      }
      navigate("/scuderie");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAF8F5] pt-safe pb-safe px-safe">
      <div className="w-full max-w-sm eq-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <img src="/icon-192.png" alt="Equiro" className="h-11 w-11 rounded-xl object-cover" />
          <span className="font-display font-extrabold text-2xl text-[#1B3B2B]">Equiro</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Crea il tuo account</h1>
        <p className="text-[#5F6B64] mt-1 mb-8">Inizia a gestire la tua scuderia.</p>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name">Nome</Label>
              <Input id="first_name" required value={f.first_name} data-testid="register-first-name"
                onChange={(e) => set("first_name", e.target.value)} placeholder="Mario" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="last_name">Cognome</Label>
              <Input id="last_name" required value={f.last_name} data-testid="register-last-name"
                onChange={(e) => set("last_name", e.target.value)} placeholder="Rossi" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={f.email} data-testid="register-email"
              onChange={(e) => set("email", e.target.value)} placeholder="nome@esempio.it" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="telefono">Telefono</Label>
            <Input id="telefono" type="tel" required value={f.telefono} data-testid="register-phone"
              onChange={(e) => set("telefono", e.target.value)} placeholder="+39 333 1234567" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={f.password}
              data-testid="register-password" onChange={(e) => set("password", e.target.value)}
              placeholder="Almeno 6 caratteri" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="confirm">Conferma password</Label>
            <Input id="confirm" type="password" required minLength={6} value={f.confirm}
              data-testid="register-confirm" onChange={(e) => set("confirm", e.target.value)}
              placeholder="Ripeti la password" className={`mt-1.5 ${mismatch ? "border-red-400" : ""}`} />
            {mismatch && <p className="text-xs text-red-600 mt-1" data-testid="password-mismatch">Le password non coincidono</p>}
          </div>
          {reqs && reqs.must_accept_terms && (
            <label className="flex items-start gap-2 text-xs text-[#5F6B64]" data-testid="register-accept-legal">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1B3B2B]" data-testid="register-accept-checkbox" checked={!!f.accept} onChange={(e) => set("accept", e.target.checked)} />
              <span>Accetto i <Link to="/termini" target="_blank" className="text-[#8C532B] font-semibold">Termini e Condizioni</Link>{reqs.privacy ? <> e dichiaro di aver letto l'<Link to="/privacy" target="_blank" className="text-[#8C532B] font-semibold">Informativa Privacy</Link></> : null}.</span>
            </label>
          )}
          {reqs && !reqs.must_accept_terms && (reqs.privacy || reqs.terms) && (
            <p className="text-xs text-[#5F6B64]" data-testid="register-legal-info">
              {reqs.privacy && <>Consulta l'<Link to="/privacy" target="_blank" className="text-[#8C532B] font-semibold">Informativa Privacy</Link>. </>}
              {reqs.terms && <>Consulta i <Link to="/termini" target="_blank" className="text-[#8C532B] font-semibold">Termini</Link>.</>}
            </p>
          )}
          <Button type="submit" disabled={loading || mismatch || (reqs?.must_accept_terms && !f.accept)} data-testid="register-submit"
            className="w-full bg-[#1B3B2B] hover:bg-[#142E1F] h-11">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Registrati
          </Button>
        </form>
        <p className="text-sm text-[#5F6B64] mt-6 text-center">
          Hai già un account?{" "}
          <Link to="/login" className="text-[#8C532B] font-semibold" data-testid="go-login">Accedi</Link>
        </p>
      </div>
    </div>
  );
}
